import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

type ToastKind = 'success' | 'error' | 'warning' | 'info';

interface ToastItem {
  id: number;
  kind: ToastKind;
  title: string;
  message?: string;
}

interface ToastApi {
  success: (title: string, message?: string) => void;
  error: (title: string, message?: string) => void;
  warning: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastApi | undefined>(undefined);

const DISMISS_MS: Record<ToastKind, number> = {
  success: 4500,
  info: 4500,
  warning: 6000,
  error: 8000,
};

const KIND_STYLE: Record<ToastKind, string> = {
  success: 'bg-emerald-50 text-emerald-950',
  error: 'bg-red-50 text-red-950',
  warning: 'bg-oxide-100 text-oxide-800',
  info: 'bg-steel-100 text-ink-950',
};

const KIND_LABEL: Record<ToastKind, string> = {
  success: 'Saved',
  error: 'Error',
  warning: 'Check',
  info: 'Notice',
};

function detailFromPayload(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const detail = (payload as { detail?: unknown; message?: unknown }).detail;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          const row = item as { msg?: unknown; loc?: unknown };
          if (typeof row.msg === 'string') {
            const loc = Array.isArray(row.loc) ? row.loc.filter((p) => p !== 'body').join('.') : '';
            return loc ? `${loc}: ${row.msg}` : row.msg;
          }
        }
        return null;
      })
      .filter((part): part is string => Boolean(part));
    if (parts.length) return parts.join(' · ');
  }
  const message = (payload as { message?: unknown }).message;
  if (typeof message === 'string' && message.trim()) return message.trim();
  return null;
}

export function errorText(err: unknown, fallback = 'Something went wrong'): string {
  const raw = err instanceof Error ? err.message : fallback;
  let clean = raw.replace(/\s+/g, ' ').trim();
  // Prefer FastAPI {"detail":"..."} / Timeero {"message":"..."} bodies.
  const jsonStart = clean.indexOf('{');
  if (jsonStart >= 0) {
    try {
      const parsed = JSON.parse(clean.slice(jsonStart)) as unknown;
      const fromDetail = detailFromPayload(parsed);
      if (fromDetail) {
        clean = fromDetail;
        // Nested JSON string inside detail (e.g. Timeero body embedded as text).
        const nestedStart = clean.indexOf('{');
        if (nestedStart >= 0) {
          try {
            const nested = detailFromPayload(JSON.parse(clean.slice(nestedStart)));
            if (nested) clean = nested;
          } catch {
            /* keep outer detail */
          }
        }
      }
    } catch {
      /* keep raw */
    }
  }
  clean = clean.replace(/^HTTP\s+\d+:\s*/i, '').trim();
  if (!clean) return fallback;
  return clean.length > 320 ? `${clean.slice(0, 319)}…` : clean;
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const timers = useRef<Map<number, number>>(new Map());
  const paused = useRef(false);
  const remaining = useRef<Map<number, { kind: ToastKind; left: number; started: number }>>(new Map());

  const clearTimer = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      clearTimer(id);
      remaining.current.delete(id);
      setItems((current) => current.filter((item) => item.id !== id));
    },
    [clearTimer],
  );

  const armTimer = useCallback(
    (id: number, kind: ToastKind, ms: number) => {
      clearTimer(id);
      remaining.current.set(id, { kind, left: ms, started: Date.now() });
      const timer = window.setTimeout(() => dismiss(id), ms);
      timers.current.set(id, timer);
    },
    [clearTimer, dismiss],
  );

  const pauseTimers = useCallback(() => {
    if (paused.current) return;
    paused.current = true;
    remaining.current.forEach((entry, id) => {
      const elapsed = Date.now() - entry.started;
      entry.left = Math.max(1000, entry.left - elapsed);
      clearTimer(id);
    });
  }, [clearTimer]);

  const resumeTimers = useCallback(() => {
    if (!paused.current) return;
    paused.current = false;
    remaining.current.forEach((entry, id) => {
      armTimer(id, entry.kind, entry.left);
    });
  }, [armTimer]);

  const push = useCallback(
    (kind: ToastKind, title: string, message?: string) => {
      const id = nextId.current++;
      setItems((current) => [...current.slice(-3), { id, kind, title, message }]);
      armTimer(id, kind, DISMISS_MS[kind]);
    },
    [armTimer],
  );

  useEffect(() => {
    const active = timers.current;
    return () => {
      active.forEach((timer) => window.clearTimeout(timer));
      active.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (title, message) => push('success', title, message),
      error: (title, message) => push('error', title, message),
      warning: (title, message) => push('warning', title, message),
      info: (title, message) => push('info', title, message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-0 top-36 z-[80] flex flex-col items-center gap-3 px-4"
        role="region"
        aria-label="Notifications"
        aria-live="polite"
      >
        {items.map((item) => (
          <article
            key={item.id}
            role={item.kind === 'error' || item.kind === 'warning' ? 'alert' : 'status'}
            className={`toast-pop pointer-events-auto relative z-[80] flex w-full max-w-md items-start gap-3 border border-ink-100 px-4 py-3 ${KIND_STYLE[item.kind]}`}
            onMouseEnter={pauseTimers}
            onMouseLeave={resumeTimers}
            onFocus={pauseTimers}
            onBlur={resumeTimers}
          >
            <span className="mt-0.5 text-xs font-bold uppercase tracking-wider text-ink-600">
              {KIND_LABEL[item.kind]}
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-display text-lg font-bold leading-snug">{item.title}</p>
              {item.message && <p className="mt-1 text-sm leading-snug">{item.message}</p>}
            </div>
            <button
              type="button"
              className="touch-target px-2 py-1 text-sm font-semibold"
              aria-label={`Dismiss ${item.title}`}
              onClick={() => dismiss(item.id)}
            >
              Close
            </button>
          </article>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
